import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { AnalyticsAPI } from '../../../api';
import {
  Value,
  BarChart
} from '../../../components/analytics';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';

const periodOptions = [
  { value: '30 days', label: 'Last 30 days' },
  { value: '90 days', label: 'Last 90 days' },
  { value: '1 year', label: 'Last year' },
  { value: 'All time', label: 'All time' },
];

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '0',
    '& .MuiInputBase-root': {
      height: '2rem',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
  },
  text: {
    marginBottom: '1rem',
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const Dashboard = ({providerUuid}) => {
  const classes = useStyles();
  const [period, setPeriod] = React.useState('30 days'); // eslint-disable-line
  const [metric, setMetric] = React.useState('Profile views');
  const [values, setValues] = React.useState(null);
  const [series, setSeries] = React.useState(null);

  const fetchValues = async () => {
    try {
      const { payload } = await AnalyticsAPI.providerValues(providerUuid, period);
      setValues(payload);
    } catch (err) {
      console.log(err); // eslint-disable-line
    }
  };

  const fetchSeries = async () => {
    try {
      const { payload } = await AnalyticsAPI.providerSeries(providerUuid, metric);
      setSeries(payload);
    } catch (err) {
      console.log(err); // eslint-disable-line
    }
  };

  const handlePeriodChange = event => {
    setPeriod(event.target.value);
  };

  const handleMetricClick = name => {
    setMetric(name);
  };

  React.useEffect(() => {
    fetchValues();
  }, [period]); // eslint-disable-line

  React.useEffect(() => {
    fetchSeries();
  }, [metric]); // eslint-disable-line

  if (values === null || series === null) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Container maxWidth='lg' fixed disableGutters={true}>
        <Grid container>
          <Grid item md={12} className={classes.row}>
            <Typography variant="body2" className={classes.text}>
              Welcome to your Clickability Account Dashboard! Here you can see and monitor the traffic and activity on your profile and listings. For more information about how to read and interpret your results, please see our article on <a href="https://community.clickability.com.au/s/article/Understanding-my-Provider-Dashboard" className={classes.link}>Understanding your Provider Dashboard</a>.
            </Typography>
            <Typography variant="body2" className={classes.text}>
              This Dashboard is for you, so if there are particular metrics or information you would like to see in future versions, <Link component={RouterLink} to="/contact">please let us know</Link>!
            </Typography>
          </Grid>
          <Grid item md={12} className={classes.row}>
            <Box display="flex" alignContent="center" justifyContent="space-between" mb="1rem" mt="2rem">
              <Typography variant="h3" color="primary">
                Summary
              </Typography>
              <Select
                value={period}
                variant="outlined"
                onChange={handlePeriodChange}
              >
                {periodOptions.map(item => (<MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>))}
              </Select>
            </Box>
            <Box display="flex" justifyContent="space-between" mb="3rem">
              {values.map(item => (
                <Value
                  key={item.title}
                  title={item.title}
                  value={item.value}
                  onClick={()=>handleMetricClick(item.title)}
                />
              ))}
            </Box>
            <Box display="flex" alignContent="center" justifyContent="space-between" mb="1rem">
              <Typography variant="h3" color="primary">
                Recent trend
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb="2rem">
              <BarChart title={metric} data={series} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

Dashboard.propTypes = {
  providerUuid: PropTypes.string.isRequired,
};

export default Dashboard;
